import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { eventsApi, familyEventsApi } from '../api/events';
import type { RsvpResponse, RsvpSummary } from '../api/events';
import { loadEventRoster } from '../api/eventRoster';
import type { EventRoster } from '../api/eventRoster';
import type { Child } from '../api/auth';
import { Button } from '../ui/Button';
import { PanelTabs } from '../ui/Tabs';
import { PeopleGrid } from '../ui/PeopleGrid';

/** Drawn at rest. The same bound FamilyPacking uses, for the same reason. */
const STACKED = 6;

/** Rows drawn in the unit table before the rest go behind a control. */
const DRAWN_ROWS = 25;

/**
 * Who is coming, and answering for the people you answer for — TROOP-46, wired to both doors by
 * TROOP-116 and split into two audiences by TROOP-137.
 *
 * `GET /units/{unitId}/events/{eventId}/rsvps/` shipped with TROOP-46 and **nothing ever called
 * it**. Only the family screen had an RSVP surface, so a Scout signed in as a member could not say
 * they were coming to their own troop's campout, and a leader could not see a roll-up at all. The
 * route was there the whole time; the screen was the missing half.
 *
 * **One control per person, never one for the family.** A Scout going and their sibling staying
 * home is ordinary, and a single answer for the household would lose a seat that needs finding.
 *
 * **Bounded, like everything else per-child.** Smoke residue has given one seeded parent
 * sixty-six children in this unit -- most of them called Rowan, with distinct person ids, so
 * deduplicating fixes nothing. Unbounded this panel rendered eleven thousand pixels and the docket
 * above it existed to stop exactly that. Six are drawn and the rest are one tap away rather than
 * truncated: a card that is never drawn is a child nobody can answer for. The same trade
 * `FamilyPacking` makes, with the same bound.
 *
 * **Answering for somebody else is the Everyone tab, and it is the trip summary's own table.** It
 * used to be a `ManageBlock` full of the same three-button cards a parent answers with, which was
 * wrong twice over: a leader chasing thirty families read thirty stacked cards instead of a column
 * they could run their eye down, and the gesture differed from the summary screen where the same
 * leader does the same job with a dropdown. Same component now, so it reads the same from either
 * place.
 *
 * **It also reaches the people who have not answered**, which the old card list could not: the RSVP
 * route returns *rows*, and silence is the absence of a row. So the unit table reads the event
 * roster, where everybody appears and `rsvpResponse` is null — and silence is a state on the row
 * rather than a person missing from the list.
 *
 * The server decides who may answer for whom: themselves always, their own children if they guard
 * them, anybody on the roster for an *adult* officer — a youth Patrol Leader runs the program and
 * does not speak for other families. `canAnswerForOthers` is that same answer, computed
 * server-side, rather than the client's old guess at `permissionLevel !== 'Member'`.
 */
export function EventRsvp({
  unitId,
  eventId,
  meId,
  children,
  canAnswerForOthers,
  onAnswered,
  onOpenSummary,
}: {
  /** Undefined on `/family` — that absence picks the guardian's door. */
  unitId?: string;
  eventId: string;
  /** The caller's own person id, so they can answer before any row exists for them. */
  meId: string;
  children: Child[];
  canAnswerForOthers: boolean;
  onAnswered: () => void;
  /** Opens the full trip summary — every column at once, with filters and paper. */
  onOpenSummary?: () => void;
}) {
  const [rsvps, setRsvps] = useState<RsvpSummary | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState('yours');

  const load = useCallback(async () => {
    try {
      setRsvps(unitId ? await eventsApi.rsvps(unitId, eventId) : await familyEventsApi.rsvps(eventId));
    } catch {
      setError('Could not load who is coming.');
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const answer = async (personId: string, response: RsvpResponse) => {
    if (unitId) {
      await eventsApi.respond(unitId, eventId, personId, response);
    } else {
      await familyEventsApi.respond(eventId, personId, response);
    }

    await load();
    onAnswered();
  };

  if (!rsvps) {
    return error ? (
      <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
        {error}
      </p>
    ) : null;
  }

  const answerFor = (id: string) => rsvps.rows.find((r) => r.personId === id)?.response;

  // Everybody this caller answers for. On the member's door that is themselves; on the parent's it
  // is their children and themselves, because a parent drives and camps too.
  const own = [
    ...children.map((c) => ({ personId: c.personId, name: c.displayName })),
    { personId: meId, name: 'You' },
  ];

  // Whoever still owes an answer comes first, so the bound never hides the question being asked.
  const ordered = [...own].sort(
    (a, b) => Number(Boolean(answerFor(a.personId))) - Number(Boolean(answerFor(b.personId))),
  );
  const drawn = showAll ? ordered : ordered.slice(0, STACKED);

  const yours = (
    <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
      {/* Four numbers, and no response is one of them. Rendering three would tell a family
          that everybody who has not opened the app is staying home. */}
      <p className="t-caption tabular" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {rsvps.going} going &middot; {rsvps.maybe} maybe &middot; {rsvps.notGoing} not going &middot;{' '}
        {rsvps.noResponse} yet to say
      </p>

      <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
        {drawn.map((person) => (
          <RsvpCard
            key={person.personId}
            name={person.name}
            current={answerFor(person.personId)}
            onAnswer={(r) => answer(person.personId, r)}
          />
        ))}

        {own.length > drawn.length && (
          <div>
            <Button variant="secondary" onClick={() => setShowAll(true)}>
              Show {own.length - drawn.length} more
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    // No heading: the docket row above is the heading.
    <div>
      <PanelTabs
        label="Who is going"
        selected={tab}
        onSelect={setTab}
        yours={yours}
        // Absent on `/family` and for anybody the server does not let answer for others — a tab
        // offering a table the route would 403 is a promise nothing keeps.
        everyone={
          canAnswerForOthers && unitId ? (
            <EveryoneTab
              unitId={unitId}
              eventId={eventId}
              onAnswered={async () => {
                await load();
                onAnswered();
              }}
              onOpenSummary={onOpenSummary}
            />
          ) : undefined
        }
      />
    </div>
  );
}

/**
 * The whole unit's answers, as the trip summary draws them.
 *
 * **The same dropdown, from the same component.** A leader recording what six families told them in
 * a car park works down one column here exactly as they do on the summary sheet, and the two cannot
 * drift because there is one {@link PeopleGrid}.
 *
 * **Silence is a row, not an omission.** The RSVP route returns answers, so a family who has said
 * nothing simply is not in it — which is the opposite of useful for the person chasing them. The
 * roster carries everybody with a null answer instead, and the cell renders that as "Not said".
 */
function EveryoneTab({
  unitId,
  eventId,
  onAnswered,
  onOpenSummary,
}: {
  unitId: string;
  eventId: string;
  onAnswered: () => void | Promise<void>;
  onOpenSummary?: () => void;
}) {
  const [roster, setRoster] = useState<EventRoster | null>(null);
  const [refused, setRefused] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const read = useCallback(async () => {
    try {
      setRoster(await loadEventRoster(unitId, eventId));
      setRefused(false);
      setFailed(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setRefused(true);
      else setFailed(true);
    }
  }, [unitId, eventId]);

  useEffect(() => {
    void read();
  }, [read]);

  if (refused) {
    return (
      <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Answering for other families is the unit&rsquo;s adult leaders.
      </p>
    );
  }

  if (failed) {
    return (
      <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
        Could not load who is coming.
      </p>
    );
  }

  if (!roster) {
    return (
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Loading who is coming...
      </p>
    );
  }

  const drawn = showAll ? roster.people : roster.people.slice(0, DRAWN_ROWS);

  return (
    <div>
      {/* The on-behalf path, said once at the top rather than on every row. */}
      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-3)' }}
      >
        For families who told you at a meeting. The row keeps that you recorded it, so it is never
        mistaken for their own answer.
      </p>

      <PeopleGrid
        people={drawn}
        columns={[]}
        going
        busy={busy}
        onGoing={(person, response) => {
          setBusy(true);
          void eventsApi
            .respond(unitId, eventId, person.personId, response)
            .then(() => read())
            .then(() => onAnswered())
            .finally(() => setBusy(false));
        }}
        // No ask columns on this tab, so these are never reached. Forms are their own row.
        onComplete={() => undefined}
        onWithdraw={() => undefined}
        onReturnForm={() => undefined}
      />

      <div
        className="flex flex-wrap items-center"
        style={{ gap: 'var(--space-4)', marginTop: 'var(--space-3)' }}
      >
        {roster.people.length > drawn.length && (
          <Button variant="secondary" onClick={() => setShowAll(true)}>
            Show {roster.people.length - drawn.length} more
          </Button>
        )}

        {onOpenSummary && (
          <button type="button" className="link-button t-caption" onClick={onOpenSummary}>
            Open the trip summary
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * One person's answer. Three buttons rather than a dropdown: the whole interaction is one tap, and
 * a family answering in a car park should not have to open a menu to do it.
 *
 * **Deliberately not the unit table's control**, and the difference is the number of rows. A parent
 * answers for one or two people and wants the answer to cost one tap; a leader works down thirty
 * and wants a column of identical controls that does not wrap onto four lines each. Same job,
 * different scale — which is the one place in this redesign where the two halves are allowed to
 * look different.
 */
function RsvpCard({
  name,
  current,
  onAnswer,
}: {
  name: string;
  current: RsvpResponse | undefined;
  onAnswer: (response: RsvpResponse) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  const answer = async (r: RsvpResponse) => {
    setBusy(true);
    try {
      await onAnswer(r);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <p className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
        {name}
      </p>

      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 var(--space-3)' }}
      >
        {current ? LABELS[current] : 'You have not said yet.'}
      </p>

      <div className="flex flex-wrap" style={{ gap: 'var(--space-2)' }}>
        {(['Going', 'Maybe', 'NotGoing'] as const).map((r) => (
          <Button
            key={r}
            variant={current === r ? 'primary' : 'secondary'}
            onClick={() => void answer(r)}
            disabledReason={busy ? 'Saving your answer.' : undefined}
          >
            {SHORT[r]}
          </Button>
        ))}
      </div>
    </div>
  );
}

const LABELS: Record<RsvpResponse, string> = {
  Going: 'Going.',
  Maybe: 'Maybe — you can change this any time.',
  NotGoing: 'Not going.',
};

const SHORT: Record<RsvpResponse, string> = {
  Going: 'Going',
  Maybe: 'Maybe',
  NotGoing: "Can't make it",
};
